// Central List State persistence manager for Pinaki Solutions CRM
// Preserves pagination page number, search query, and active filters across list <-> detail navigation and page refreshes.

import { useState, useCallback } from "react";

const STORAGE_PREFIX = "pinaki_list_state_";
const memoryCache = new Map();

/**
 * Get the saved state for a given section/list key.
 * @param {string} sectionKey
 * @param {object} defaultState
 * @returns {object}
 */
export function getListState(sectionKey, defaultState = {}) {
  if (!sectionKey) return { ...defaultState };
  try {
    const mem = memoryCache.get(sectionKey);
    if (mem !== undefined) {
      return { ...defaultState, ...mem };
    }
    const raw = typeof window !== "undefined" && window.sessionStorage
      ? window.sessionStorage.getItem(`${STORAGE_PREFIX}${sectionKey}`)
      : null;
    if (raw) {
      const parsed = JSON.parse(raw);
      memoryCache.set(sectionKey, parsed);
      return { ...defaultState, ...parsed };
    }
  } catch (err) {
    console.warn(`[listState] Failed to read state for ${sectionKey}:`, err);
  }
  return { ...defaultState };
}

/**
 * Save partial or updated state for a given section/list key.
 * @param {string} sectionKey
 * @param {object|function} stateOrUpdater
 */
export function setListState(sectionKey, stateOrUpdater) {
  if (!sectionKey) return;
  try {
    const current = getListState(sectionKey, {});
    const updated = typeof stateOrUpdater === "function"
      ? stateOrUpdater(current)
      : { ...current, ...stateOrUpdater };
    memoryCache.set(sectionKey, updated);
    if (typeof window !== "undefined" && window.sessionStorage) {
      window.sessionStorage.setItem(`${STORAGE_PREFIX}${sectionKey}`, JSON.stringify(updated));
    }
    return updated;
  } catch (err) {
    console.warn(`[listState] Failed to save state for ${sectionKey}:`, err);
  }
}

/**
 * Clear the saved state for a given section/list key.
 * @param {string} sectionKey
 */
export function clearListState(sectionKey) {
  if (!sectionKey) return;
  try {
    memoryCache.delete(sectionKey);
    if (typeof window !== "undefined" && window.sessionStorage) {
      window.sessionStorage.removeItem(`${STORAGE_PREFIX}${sectionKey}`);
    }
  } catch (err) {}
}

/**
 * Custom hook to manage a persisted state field for a section.
 * Behaves like useState, but initializes from and saves to listState.
 *
 * @param {string} sectionKey - e.g. "orders", "leads", "clients"
 * @param {string} fieldName - e.g. "search", "stage", "statusFilter"
 * @param {*} defaultValue - default initial value if none saved
 */
export function useSavedState(sectionKey, fieldName, defaultValue) {
  const [value, setValueState] = useState(() => {
    if (!sectionKey) return defaultValue;
    const saved = getListState(sectionKey, {});
    return saved[fieldName] !== undefined ? saved[fieldName] : defaultValue;
  });

  const setValue = useCallback((updater) => {
    setValueState((prev) => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      if (sectionKey) {
        setListState(sectionKey, { [fieldName]: next });
      }
      return next;
    });
  }, [sectionKey, fieldName]);

  return [value, setValue];
}
